import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CompanySpaceOrganizationUnitCreateResponse,
  CompanySpaceOrganizationUnitUpdateResponse,
  CompanySpacesOrganizationUnitsGetCategoryEnum,
  CompanySpacesOrganizationUnitsGetRequest,
  OrganizationUnitApi,
  OrganizationUnitLeaderApi,
  OrganizationUnitLeaderCreate,
  OrganizationUnitLeaderUpdate,
  OrganizationUnitsSubtreeDeletionImpactDeletionImpactResponse,
  OrganizationUnitsMembershipSummaryMembershipSummaryResponse,
  OrganizationUnitUpdate,
  UserApi,
} from '../generated-api';
import { API_CONFIG } from '../app/api-config';
import { getPageInfo } from '../helpers/api';
import { WorkplaceUnit } from '../helpers/workplaceTree';
import { WorkplaceErrorField, workplaceErrorField } from '../helpers/workplaceErrors';
import { ymdToApiDate } from '../helpers/date';

/** the values the destroy endpoint accepts; see SubtreeDeletion::STRATEGIES */
export type DeleteUsersStrategy = 'nullify' | 'reassign';

export interface DeleteWorkplaceParams {
  id: number;
  /** delete the whole subtree; without it the children are rooted instead */
  includeDescendants: boolean;
  strategy?: DeleteUsersStrategy;
  /** where the members are moved when strategy is 'reassign' */
  targetId?: number;
}

/**
 * The generated client models DELETE without its options, so the destroy
 * parameters are sent through the same request pipeline by hand.
 */
class WorkplacesApi extends OrganizationUnitApi {
  async destroy(params: DeleteWorkplaceParams): Promise<void> {
    const query: Record<string, string | number | boolean> = {};
    if (params.includeDescendants) {
      query.include_descendants = true;
      query.strategy = params.strategy || 'nullify';
      if (params.strategy === 'reassign' && params.targetId) {
        query.target_id = params.targetId;
      }
    }
    await this.request({
      path: '/company_spaces/organization_units/' + encodeURIComponent(String(params.id)),
      method: 'DELETE',
      headers: {},
      query,
    });
  }
}

/**
 * The users index says how many records the filter matched in a `Total-Records` header,
 * so a page of one is all it takes to count them. It is sent by hand because the counts
 * are headers -- the generated method hands back the parsed body -- and because "nobody's
 * workplace" is the blank array `filter_department[]=`, which the generated client would
 * flatten into a plain empty string and the API would then read as a name to match.
 */
class UserCountApi extends UserApi {
  async count(query: Record<string, string | number>): Promise<number | undefined> {
    const response = await this.request({
      path: '/company_spaces/users',
      method: 'GET',
      headers: {},
      // the same population the Users screen lists by default -- archived people are
      // counted there under Archived, not among the organization's users
      query: { page: 1, paginates_per: 1, filter_state: 'not_revoked', ...query },
    });
    return getPageInfo(response).total;
  }
}

const organizationUnitApi = new WorkplacesApi(API_CONFIG);
const leaderApi = new OrganizationUnitLeaderApi(API_CONFIG);
const userCountApi = new UserCountApi(API_CONFIG);

/** the two independent sets of assignments a workplace carries */
export type WorkplaceAssignmentKind = 'leadership' | 'budget';
export type WorkplaceAssignmentRole = 'leader' | 'deputy';

/**
 * Leadership and financial approval are the same resource behind two generated
 * method names; the kind picks the endpoint rather than travelling in the body.
 */
const assignmentEndpoints: Record<
  WorkplaceAssignmentKind,
  {
    create: (organizationUnitId: number, body: OrganizationUnitLeaderCreate) => Promise<unknown>;
    update: (
      organizationUnitId: number,
      id: string,
      body: OrganizationUnitLeaderUpdate
    ) => Promise<unknown>;
    remove: (organizationUnitId: number, id: string) => Promise<void>;
  }
> = {
  leadership: {
    create: (organizationUnitId, body) =>
      leaderApi.companySpacesOrganizationUnitsOrganizationUnitIdOrganizationUnitLeadersPost({
        organizationUnitId,
        body,
      }),
    update: (organizationUnitId, id, body) =>
      leaderApi.companySpacesOrganizationUnitsOrganizationUnitIdOrganizationUnitLeadersIdPut({
        organizationUnitId,
        id,
        body,
      }),
    remove: (organizationUnitId, id) =>
      leaderApi.companySpacesOrganizationUnitsOrganizationUnitIdOrganizationUnitLeadersIdDelete({
        organizationUnitId,
        id,
      }),
  },
  budget: {
    create: (organizationUnitId, body) =>
      leaderApi.companySpacesOrganizationUnitsOrganizationUnitIdOrganizationUnitFinancialApproversPost({
        organizationUnitId,
        body,
      }),
    update: (organizationUnitId, id, body) =>
      leaderApi.companySpacesOrganizationUnitsOrganizationUnitIdOrganizationUnitFinancialApproversIdPut({
        organizationUnitId,
        id,
        body,
      }),
    remove: (organizationUnitId, id) =>
      leaderApi.companySpacesOrganizationUnitsOrganizationUnitIdOrganizationUnitFinancialApproversIdDelete(
        { organizationUnitId, id }
      ),
  },
};

/** Pageable#MAX_PAGE_SIZE on the backend; larger values are rejected */
const PAGE_SIZE = 1000;
/** stops a broken pagination contract from looping forever */
const MAX_PAGES = 20;

export interface WorkplacesFilter {
  search: string;
  withoutLeader: boolean;
  withoutMembers: boolean;
}

export const emptyWorkplacesFilter: WorkplacesFilter = {
  search: '',
  withoutLeader: false,
  withoutMembers: false,
};

export const isFilterActive = (filter: WorkplacesFilter): boolean =>
  !!filter.search.trim() || filter.withoutLeader || filter.withoutMembers;

/** whether the rows on screen already belong to the filter the user sees */
export const isSameFilter = (a?: WorkplacesFilter | null, b?: WorkplacesFilter | null): boolean =>
  !!a &&
  !!b &&
  a.search.trim() === b.search.trim() &&
  a.withoutLeader === b.withoutLeader &&
  a.withoutMembers === b.withoutMembers;

const fetchAllPages = async (
  params: Omit<CompanySpacesOrganizationUnitsGetRequest, 'page' | 'perPage'>
): Promise<WorkplaceUnit[]> => {
  const units: WorkplaceUnit[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const chunk = await organizationUnitApi.companySpacesOrganizationUnitsGet({
      ...params,
      page,
      perPage: PAGE_SIZE,
    });
    units.push(...chunk);
    if (chunk.length < PAGE_SIZE) {
      break;
    }
  }
  return units;
};

/**
 * The whole workplace tree in one go. Structure always comes from here, never from a
 * filtered response -- a filtered response omits the ancestors of its own matches.
 */
export const fetchWorkplaces = createAsyncThunk('workplaces/fetch', async () => {
  return await fetchAllPages({
    category: CompanySpacesOrganizationUnitsGetCategoryEnum.Department,
  });
});

/**
 * Search and filters stay on the server (one source of truth for diacritics-insensitive
 * matching and for the time validity of leadership); we only keep the matching ids and
 * project them onto the tree we already hold.
 */
export const fetchWorkplaceMatches = createAsyncThunk(
  'workplaces/matches',
  async (filter: WorkplacesFilter) => {
    const units = await fetchAllPages({
      category: CompanySpacesOrganizationUnitsGetCategoryEnum.Department,
      search: filter.search.trim() || undefined,
      withoutLeader: filter.withoutLeader || undefined,
      withoutMembers: filter.withoutMembers || undefined,
    });
    return units.map((unit) => unit.id);
  }
);

export interface WorkplaceStats {
  users?: number;
  withoutWorkplace?: number;
}

/**
 * Numbers for the strip above the editor. They only describe the screen, so a refusal
 * (a non-admin, a request that fell over) leaves them out rather than reporting a
 * failure the user cannot act on.
 */
export const fetchWorkplaceStats = createAsyncThunk(
  'workplaces/stats',
  async (): Promise<WorkplaceStats> => {
    try {
      return {
        users: await userCountApi.count({}),
        // the blank department is the denormalized mirror of "no membership anywhere"
        withoutWorkplace: await userCountApi.count({ 'filter_department[]': '' }),
      };
    } catch (error) {
      return {};
    }
  }
);

export const updateWorkplace = createAsyncThunk<
  CompanySpaceOrganizationUnitUpdateResponse,
  { id: number; body: OrganizationUnitUpdate },
  { rejectValue: WorkplaceErrorField }
>('workplaces/update', async (params, { rejectWithValue }) => {
  try {
    return await organizationUnitApi.companySpacesOrganizationUnitsIdPut({
      id: String(params.id),
      body: params.body,
    });
  } catch (error) {
    const field = workplaceErrorField(error);
    // a recognised field error is shown at the field, so the global error toast is skipped
    if (field) {
      return rejectWithValue(field);
    }
    throw error;
  }
});

export const createWorkplace = createAsyncThunk<
  CompanySpaceOrganizationUnitCreateResponse,
  { name: string; code: string | null; parentId: number | null },
  { rejectValue: WorkplaceErrorField }
>('workplaces/create', async (params, { rejectWithValue }) => {
  try {
    return await organizationUnitApi.companySpacesOrganizationUnitsPost({
      body: {
        category: CompanySpacesOrganizationUnitsGetCategoryEnum.Department,
        name: params.name,
        code: params.code,
        parent_id: params.parentId,
      },
    });
  } catch (error) {
    const field = workplaceErrorField(error);
    if (field) {
      return rejectWithValue(field);
    }
    throw error;
  }
});

/** how many units and members a delete would touch, for the confirmation dialog */
export const fetchDeletionImpact = createAsyncThunk(
  'workplaces/deletionImpact',
  async (id: number): Promise<OrganizationUnitsSubtreeDeletionImpactDeletionImpactResponse> => {
    return await organizationUnitApi.companySpacesOrganizationUnitsIdDeletionImpactGet({
      id: String(id),
    });
  }
);

/** read-only membership breakdown for the Membership tab of the detail */
export const fetchMembershipSummary = createAsyncThunk(
  'workplaces/membershipSummary',
  async (id: number): Promise<OrganizationUnitsMembershipSummaryMembershipSummaryResponse> => {
    return await organizationUnitApi.companySpacesOrganizationUnitsIdMembershipSummaryGet({
      id: String(id),
    });
  }
);

export const deleteWorkplace = createAsyncThunk(
  'workplaces/delete',
  async (params: DeleteWorkplaceParams) => {
    await organizationUnitApi.destroy(params);
    return params.id;
  }
);

export interface SaveAssignmentParams {
  unitId: number;
  kind: WorkplaceAssignmentKind;
  role: WorkplaceAssignmentRole;
  /** the assignment already in this slot, if any */
  currentId?: number;
  userInCompanySpaceId: number;
  /** both in the `yyyy-MM-dd` shape a date input produces */
  validFrom: string;
  validTo: string | null;
}

/**
 * Puts a person into one slot (leader or deputy, leadership or budget). The
 * update endpoint carries only the dates and the role, never the person, so
 * swapping someone out means destroying the old assignment and creating a new
 * one. The old one goes first: the backend accepts several assignments of the
 * same role, and a leftover duplicate would be invisible in a UI that shows one
 * name per slot, whereas an empty slot after a failed create is plain to see.
 */
export const saveWorkplaceAssignment = createAsyncThunk(
  'workplaces/saveAssignment',
  async (params: SaveAssignmentParams) => {
    const endpoints = assignmentEndpoints[params.kind];
    const validFrom = ymdToApiDate(params.validFrom) || new Date();
    const validTo = params.validTo ? ymdToApiDate(params.validTo) ?? null : null;

    if (params.currentId) {
      await endpoints.remove(params.unitId, String(params.currentId));
    }
    await endpoints.create(params.unitId, {
      user_in_company_space_id: params.userInCompanySpaceId,
      role: params.role,
      valid_from: validFrom,
      valid_to: validTo,
    } as OrganizationUnitLeaderCreate);
  }
);

/** only the validity moves here, so the assignment keeps its id and its audit trail */
export const updateWorkplaceAssignmentValidity = createAsyncThunk(
  'workplaces/updateAssignmentValidity',
  async (params: {
    unitId: number;
    kind: WorkplaceAssignmentKind;
    id: number;
    validFrom: string;
    validTo: string | null;
  }) => {
    await assignmentEndpoints[params.kind].update(params.unitId, String(params.id), {
      valid_from: ymdToApiDate(params.validFrom),
      valid_to: params.validTo ? ymdToApiDate(params.validTo) ?? null : null,
    } as OrganizationUnitLeaderUpdate);
  }
);

export const deleteWorkplaceAssignment = createAsyncThunk(
  'workplaces/deleteAssignment',
  async (params: { unitId: number; kind: WorkplaceAssignmentKind; id: number }) => {
    await assignmentEndpoints[params.kind].remove(params.unitId, String(params.id));
  }
);

export interface WorkplacesState {
  items: WorkplaceUnit[];
  isLoading: boolean;
  isLoaded: boolean;
  loadError?: string;
  /** null means no filter is active and the whole tree is shown */
  matchedIds: number[] | null;
  /** the filter the matchedIds were fetched for */
  appliedFilter?: WorkplacesFilter;
  /** only the newest filter request may write the result */
  matchRequestId?: string;
  isFiltering: boolean;
  filterError?: string;
  isSaving: boolean;
  /** counts for the strip above the editor; absent until they arrive, or if they cannot */
  stats: WorkplaceStats;
}

const initialState: WorkplacesState = {
  items: [],
  isLoading: false,
  isLoaded: false,
  matchedIds: null,
  isFiltering: false,
  isSaving: false,
  stats: {},
};

export const workplacesSlice = createSlice({
  name: 'workplaces',
  initialState,
  reducers: {
    clearWorkplaceMatches: (state) => {
      state.matchRequestId = undefined;
      state.matchedIds = null;
      state.appliedFilter = undefined;
      state.isFiltering = false;
      state.filterError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchWorkplaceStats.fulfilled,
      (state, action: PayloadAction<WorkplaceStats>) => {
        state.stats = action.payload;
      }
    );
    builder.addCase(fetchWorkplaces.pending, (state) => {
      state.isLoading = true;
      state.loadError = undefined;
    });
    builder.addCase(fetchWorkplaces.fulfilled, (state, action: PayloadAction<WorkplaceUnit[]>) => {
      state.isLoading = false;
      state.isLoaded = true;
      state.items = action.payload;
    });
    builder.addCase(fetchWorkplaces.rejected, (state, action) => {
      state.isLoading = false;
      state.loadError = action.error.message || 'error';
    });

    builder.addCase(fetchWorkplaceMatches.pending, (state, action) => {
      state.matchRequestId = action.meta.requestId;
      state.isFiltering = true;
      state.filterError = undefined;
    });
    builder.addCase(fetchWorkplaceMatches.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.matchRequestId) {
        return;
      }
      state.isFiltering = false;
      state.matchedIds = action.payload;
      state.appliedFilter = action.meta.arg;
    });
    builder.addCase(fetchWorkplaceMatches.rejected, (state, action) => {
      if (action.meta.requestId !== state.matchRequestId) {
        return;
      }
      state.isFiltering = false;
      state.filterError = action.error.message || 'error';
      // the filter is done, just unsuccessfully -- otherwise the tree stays busy forever
      state.appliedFilter = action.meta.arg;
    });

    builder.addCase(updateWorkplace.pending, (state) => {
      state.isSaving = true;
    });
    builder.addCase(updateWorkplace.fulfilled, (state, action) => {
      state.isSaving = false;
      const updated = action.payload as unknown as WorkplaceUnit;
      state.items = state.items.map((item) => (item.id === updated.id ? updated : item));
    });
    builder.addCase(updateWorkplace.rejected, (state) => {
      state.isSaving = false;
    });

    builder.addCase(createWorkplace.pending, (state) => {
      state.isSaving = true;
    });
    builder.addCase(createWorkplace.fulfilled, (state, action) => {
      state.isSaving = false;
      state.items = [...state.items, action.payload as unknown as WorkplaceUnit];
    });
    builder.addCase(createWorkplace.rejected, (state) => {
      state.isSaving = false;
    });

    builder.addCase(deleteWorkplace.pending, (state) => {
      state.isSaving = true;
    });
    builder.addCase(deleteWorkplace.fulfilled, (state) => {
      state.isSaving = false;
    });
    builder.addCase(deleteWorkplace.rejected, (state) => {
      state.isSaving = false;
    });

    // the three assignment thunks only ever move the busy flag; the rows themselves
    // come back with the refetch the caller fires afterwards
    [
      saveWorkplaceAssignment,
      updateWorkplaceAssignmentValidity,
      deleteWorkplaceAssignment,
    ].forEach((thunk) => {
      builder.addCase(thunk.pending, (state) => {
        state.isSaving = true;
      });
      builder.addCase(thunk.fulfilled, (state) => {
        state.isSaving = false;
      });
      builder.addCase(thunk.rejected, (state) => {
        state.isSaving = false;
      });
    });
  },
});

export const workplacesReducer = workplacesSlice.reducer;
export const { clearWorkplaceMatches } = workplacesSlice.actions;
